import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  downloadFestival,
  isFestivalOffline,
  isInDownloadWindow,
  isPastPurge,
  purgeFestival,
  refreshFestivalIfChanged,
} from '../../lib/festivalOffline';
import { useNetworkStatus } from '../useNetworkStatus';
import { mapStore } from '../../lib/map/mapStore';
import { useMyFestivalMaps } from './useMyFestivalMaps';
import { useCancelledEvents } from './useCancelledEvents';
import { FESTIVAL_MAPS_ENABLED } from '../../config/festivalMaps';

/**
 * Your festivals, on the phone before you get there: from three days before the first day, each festival you are
 * going to that has a map gets its offline set (map, lineup, your picks - lib/festivalOffline) on a reachable network,
 * one at a time so it never competes with what is on screen. Three days after a festival, its set is deleted.
 * Anything not fetched yet can be saved by hand from the Maps list ("Download").
 */
export function usePrefetchFestivalMapsForToday(userId) {
  const queryClient = useQueryClient();
  const { prefetchAllowed } = useNetworkStatus();
  const { data: mine } = useMyFestivalMaps(FESTIVAL_MAPS_ENABLED ? userId : null);
  // Not for a festival that was cancelled: list_my_festival_maps joins events without
  // looking at status, and a few hundred KB spent on a map nobody will open is exactly
  // the bandwidth this hook exists to protect.
  const cancelledEvents = useCancelledEvents();
  // Coming back to the app (unlocking at the festival) re-checks for a newer map or set times.
  const [resumeTick, setResumeTick] = useState(0);
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) setResumeTick((n) => n + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, []);

  useEffect(() => {
    if (!FESTIVAL_MAPS_ENABLED || !mine?.length) return undefined;
    for (const m of mine) if (isPastPurge(m)) purgeFestival(queryClient, m.event_id);
    if (!prefetchAllowed) return undefined;
    const due = mine.filter((m) => !cancelledEvents.has(m.event_id) && isInDownloadWindow(m));
    if (!due.length) return undefined;

    let cancelled = false;
    (async () => {
      await mapStore.ready(); // "on the phone" is answered from the store's index: read it before deciding to download
      for (const m of due) {
        if (cancelled) return;
        // Not on the phone yet: download it. On the phone: a cheap version check re-downloads only what changed.
        if (!isFestivalOffline(queryClient, m.event_id)) {
          const ok = await downloadFestival(queryClient, m.event_id, userId);
          if (!ok) continue; // one map failing (a flaky fetch) must not skip the next event's; offline, each fails fast and the next mount tries again
        } else {
          await refreshFestivalIfChanged(queryClient, m.event_id, userId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mine, prefetchAllowed, queryClient, cancelledEvents, userId, resumeTick]);
}
